import {
    useCallback,
    useMemo,
} from 'react'
import {
    createBrowserRouter, Navigate, Outlet, RouterProvider,
} from 'react-router-dom'
import {
    useJWTToken,
    useUserRoles,
} from 'app/Auth'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    App,
} from 'app/enums/domain.enums'
import hasPermissionFunction, {
    RolesTypes,
} from 'app/utils/hasPermission'

import {
    RouterErrorBoundary,
} from 'app/ErrorBoundaries'
import queryLoaderInit from 'app/requests/skyNetQueryLoader'
import {
    createSkyNetRoutes, injectSkyNetConfigParams,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'
import DemoRoutes from 'app/PoC/DataGridDemoPages/Demo'
import Apps from '../Apps'
import Root from '../Root'
import {
    APPS,
} from './SkyNetRoutes.types'

const routes = [
    {
        path: '/',
        element: <Root />,
        children: [
            {
                path: `${APPS}`,
                element: <Navigate
                    to={`/${APPS}/${App.Dashboard}`}
                    replace
                />,
            },
            {
                path: `${APPS}`,
                element: <Outlet />,
                children: createSkyNetRoutes({
                    routesConfig: Apps,
                }),
            },
            {
                path: `${APPS}/${CommonRoutes.Demo}/${CommonRoutes.Asterisk}`,
                element: <Outlet />,
                children: DemoRoutes,
                accessOnly: ['dev_team'],
            },
            {
                path: CommonRoutes.Slash,
                element: <Navigate
                    to={`/${APPS}`}
                    replace
                />,
            },
        ],
        errorElement: <RouterErrorBoundary />,
    },
    {
        path: CommonRoutes.Slash,
        element: <Navigate
            to={`/${APPS}`}
            replace
        />,
    },
    {
        path: CommonRoutes.Asterisk,
        element: <Navigate
            to={`/${APPS}`}
            replace
        />,
    },
]

export default function SkyNetRouterProvider() {
    const token = useJWTToken()
    const roles = useUserRoles()

    const hasPermission = useCallback((permissions: RolesTypes[]) => {
        return hasPermissionFunction(permissions, roles)
    }, [roles])

    const skyNetQueryLoader = useMemo(() => {
        return queryLoaderInit(token)
    }, [token])

    const appRoutes = useMemo(() => {
        return injectSkyNetConfigParams(routes, {
            loader: skyNetQueryLoader,
            hasPermission,
        })
    }, [
        hasPermission,
        skyNetQueryLoader,
    ])

    return (
        <RouterProvider
            router={
                createBrowserRouter(appRoutes)
            }
            future={{
                v7_startTransition: true,
            }}
        />
    )
}
